import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { XIcon, CaretDownIcon, CaretRightIcon } from '@phosphor-icons/react';
import { Badge } from '@/components/ui/badge';
import {
  validateEmail,
  deduplicateEmails,
  validateEmailList,
} from '@/utils/emailValidation';
import { cn } from '@/lib/utils';

interface SendTableEmailModalProps {
  open: boolean;
  onClose: () => void;
  onSend: (params: {
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body: string;
    filename: string;
  }) => void;
  defaultSubject: string;
  defaultBody: string;
  defaultFilename: string;
  isLoading?: boolean;
}

function SendTableEmailModal({
  open,
  onClose,
  onSend,
  defaultSubject,
  defaultBody,
  defaultFilename,
  isLoading = false,
}: SendTableEmailModalProps) {
  // Form state
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [filename, setFilename] = useState(defaultFilename);

  // Input field state for adding emails
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');

  // Expandable sections
  const [showCC, setShowCC] = useState(false);
  const [showBCC, setShowBCC] = useState(false);

  // Derived state: fields with emails should always be visible
  const shouldShowCC = showCC || ccEmails.length > 0;
  const shouldShowBCC = showBCC || bccEmails.length > 0;

  // Validation state
  const [errors, setErrors] = useState<{
    to?: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    body?: string;
    filename?: string;
  }>({});

  // Handle adding email to a list
  const handleAddEmail = useCallback(
    (
      input: string,
      emailList: string[],
      setEmailList: (emails: string[]) => void,
      setInput: (value: string) => void,
      field: 'to' | 'cc' | 'bcc',
    ) => {
      const trimmed = input.trim();
      if (!trimmed) return;

      // Parse comma-separated emails
      const newEmails = trimmed
        .split(',')
        .map((e) => e.trim())
        .filter((e) => e.length > 0);

      // Validate all emails
      const invalidEmails = newEmails.filter((e) => !validateEmail(e));
      if (invalidEmails.length > 0) {
        setErrors((prev) => ({
          ...prev,
          [field]: `Invalid email(s): ${invalidEmails.join(', ')}`,
        }));
        return;
      }

      // Add to list (deduplicate)
      const combined = [...emailList, ...newEmails];
      const deduplicated = deduplicateEmails(combined);
      setEmailList(deduplicated);
      setInput('');

      // Clear errors
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    },
    [],
  );

  // Handle removing email from a list
  const handleRemoveEmail = useCallback(
    (
      email: string,
      emailList: string[],
      setEmailList: (emails: string[]) => void,
    ) => {
      setEmailList(emailList.filter((e) => e !== email));
    },
    [],
  );

  // Handle key press in email input fields
  const handleEmailInputKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      input: string,
      emailList: string[],
      setEmailList: (emails: string[]) => void,
      setInput: (value: string) => void,
      field: 'to' | 'cc' | 'bcc',
    ) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault();
        handleAddEmail(input, emailList, setEmailList, setInput, field);
      }
    },
    [handleAddEmail],
  );

  // Validate form
  const validationErrors = useMemo(() => {
    const newErrors: typeof errors = {};

    if (toEmails.length === 0) {
      newErrors.to = 'At least one recipient is required';
    }

    if (!subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!body.trim()) {
      newErrors.body = 'Message body is required';
    }

    if (!filename.trim()) {
      newErrors.filename = 'Filename is required';
    }

    return newErrors;
  }, [toEmails, subject, body, filename]);

  const isValid = Object.keys(validationErrors).length === 0;

  // Handle send
  const handleSend = useCallback(() => {
    if (!isValid) {
      setErrors(validationErrors);
      return;
    }

    onSend({
      to: toEmails,
      cc: ccEmails.length > 0 ? ccEmails : undefined,
      bcc: bccEmails.length > 0 ? bccEmails : undefined,
      subject,
      body,
      filename,
    });
  }, [
    isValid,
    validationErrors,
    toEmails,
    ccEmails,
    bccEmails,
    subject,
    body,
    filename,
    onSend,
  ]);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setToEmails([]);
      setCcEmails([]);
      setBccEmails([]);
      setSubject(defaultSubject);
      setBody(defaultBody);
      setFilename(defaultFilename);
      setToInput('');
      setCcInput('');
      setBccInput('');
      setShowCC(false);
      setShowBCC(false);
      setErrors({});
    }
  }, [open, defaultSubject, defaultBody, defaultFilename]);

  // Reset form on close
  const handleClose = useCallback(() => {
    setToEmails([]);
    setCcEmails([]);
    setBccEmails([]);
    setSubject(defaultSubject);
    setBody(defaultBody);
    setFilename(defaultFilename);
    setToInput('');
    setCcInput('');
    setBccInput('');
    setShowCC(false);
    setShowBCC(false);
    setErrors({});
    onClose();
  }, [defaultSubject, defaultBody, defaultFilename, onClose]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send Table via Email</DialogTitle>
          <DialogDescription>
            Compose and send an email with the table data as a CSV attachment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* To Field */}
          <div className="space-y-2">
            <Label htmlFor="to">
              To <span className="text-destructive">*</span>
            </Label>
            <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px] items-center">
              {toEmails.map((email) => (
                <Badge
                  key={email}
                  variant="secondary"
                  className="flex items-center gap-1"
                >
                  {email}
                  <button
                    type="button"
                    onClick={() =>
                      handleRemoveEmail(email, toEmails, setToEmails)
                    }
                    className="ml-1 hover:text-destructive"
                  >
                    <XIcon className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <Input
                id="to"
                value={toInput}
                onChange={(e) => setToInput(e.target.value)}
                onKeyDown={(e) =>
                  handleEmailInputKeyDown(
                    e,
                    toInput,
                    toEmails,
                    setToEmails,
                    setToInput,
                    'to',
                  )
                }
                onBlur={() =>
                  handleAddEmail(
                    toInput,
                    toEmails,
                    setToEmails,
                    setToInput,
                    'to',
                  )
                }
                placeholder="Enter email addresses (comma-separated)"
                className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 min-w-[200px]"
                disabled={isLoading}
              />
            </div>
            {(errors.to || validationErrors.to) && (
              <p className="text-sm text-destructive">
                {errors.to || validationErrors.to}
              </p>
            )}
          </div>

          {/* Add CC Button */}
          {!shouldShowCC && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowCC(true)}
              disabled={isLoading}
            >
              <CaretRightIcon className="h-4 w-4 mr-1" />
              Add CC
            </Button>
          )}

          {/* CC Field */}
          {shouldShowCC && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="cc">CC</Label>
                {ccEmails.length === 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowCC(false);
                      setCcInput('');
                    }}
                    disabled={isLoading}
                  >
                    <CaretDownIcon className="h-4 w-4 mr-1" />
                    Hide
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px] items-center">
                {ccEmails.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveEmail(email, ccEmails, setCcEmails)
                      }
                      className="ml-1 hover:text-destructive"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <Input
                  id="cc"
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  onKeyDown={(e) =>
                    handleEmailInputKeyDown(
                      e,
                      ccInput,
                      ccEmails,
                      setCcEmails,
                      setCcInput,
                      'cc',
                    )
                  }
                  onBlur={() =>
                    handleAddEmail(
                      ccInput,
                      ccEmails,
                      setCcEmails,
                      setCcInput,
                      'cc',
                    )
                  }
                  placeholder="Enter email addresses (comma-separated)"
                  className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 min-w-[200px]"
                  disabled={isLoading}
                />
              </div>
              {errors.cc && (
                <p className="text-sm text-destructive">{errors.cc}</p>
              )}
            </div>
          )}

          {/* Add BCC Button */}
          {!shouldShowBCC && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowBCC(true)}
              disabled={isLoading}
            >
              <CaretRightIcon className="h-4 w-4 mr-1" />
              Add BCC
            </Button>
          )}

          {/* BCC Field */}
          {shouldShowBCC && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="bcc">BCC</Label>
                {bccEmails.length === 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowBCC(false);
                      setBccInput('');
                    }}
                    disabled={isLoading}
                  >
                    <CaretDownIcon className="h-4 w-4 mr-1" />
                    Hide
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[42px] items-center">
                {bccEmails.map((email) => (
                  <Badge
                    key={email}
                    variant="secondary"
                    className="flex items-center gap-1"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() =>
                        handleRemoveEmail(email, bccEmails, setBccEmails)
                      }
                      className="ml-1 hover:text-destructive"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
                <Input
                  id="bcc"
                  value={bccInput}
                  onChange={(e) => setBccInput(e.target.value)}
                  onKeyDown={(e) =>
                    handleEmailInputKeyDown(
                      e,
                      bccInput,
                      bccEmails,
                      setBccEmails,
                      setBccInput,
                      'bcc',
                    )
                  }
                  onBlur={() =>
                    handleAddEmail(
                      bccInput,
                      bccEmails,
                      setBccEmails,
                      setBccInput,
                      'bcc',
                    )
                  }
                  placeholder="Enter email addresses (comma-separated)"
                  className="flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 min-w-[200px]"
                  disabled={isLoading}
                />
              </div>
              {errors.bcc && (
                <p className="text-sm text-destructive">{errors.bcc}</p>
              )}
            </div>
          )}

          {/* Subject Field */}
          <div className="space-y-2">
            <Label htmlFor="subject">
              Subject <span className="text-destructive">*</span>
            </Label>
            <Input
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject"
              disabled={isLoading}
            />
            {validationErrors.subject && (
              <p className="text-sm text-destructive">
                {validationErrors.subject}
              </p>
            )}
          </div>

          {/* Filename Field */}
          <div className="space-y-2">
            <Label htmlFor="filename">
              Attachment Filename <span className="text-destructive">*</span>
            </Label>
            <Input
              id="filename"
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              placeholder="Enter filename (e.g., rebate-analysis)"
              disabled={isLoading}
            />
            {validationErrors.filename && (
              <p className="text-sm text-destructive">
                {validationErrors.filename}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              The .csv extension will be added automatically if not provided.
            </p>
          </div>

          {/* Body Field */}
          <div className="space-y-2">
            <Label htmlFor="body">
              Message <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Enter email message"
              rows={6}
              disabled={isLoading}
            />
            {validationErrors.body && (
              <p className="text-sm text-destructive">
                {validationErrors.body}
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            disabled={!isValid || isLoading}
          >
            {isLoading ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SendTableEmailModal;
